/**
 * Utility functions to convert between text markers and HTML markers for TipTap editor
 */

interface MarkerPattern {
  regex: RegExp;
  markerName: string;
  label: string;
  icon: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

const markerPatterns: MarkerPattern[] = [
  {
    regex: /\[CONTROL POINT:\s*([^\]]+)\]/g,
    markerName: 'controlPoint',
    label: 'CONTROL POINT',
    icon: '🔍',
    bgColor: '#fef9c3',
    textColor: '#854d0e',
    borderColor: '#facc15',
  },
  {
    regex: /\[GAP IDENTIFIED:\s*([^\]]+)\]/g,
    markerName: 'gapIdentified',
    label: 'GAP IDENTIFIED',
    icon: '⚠️',
    bgColor: '#fee2e2',
    textColor: '#991b1b',
    borderColor: '#ef4444',
  },
  {
    regex: /\[SCREENSHOT NEEDED:\s*([^\]]+)\]/g,
    markerName: 'screenshotNeeded',
    label: 'SCREENSHOT NEEDED',
    icon: '📸',
    bgColor: '#dbeafe',
    textColor: '#1e3a8a',
    borderColor: '#3b82f6',
  },
  {
    regex: /\[FLOWCHART NEEDED:\s*([^\]]+)\]/g,
    markerName: 'flowchartNeeded',
    label: 'FLOWCHART NEEDED',
    icon: '📊',
    bgColor: '#d1fae5',
    textColor: '#065f46',
    borderColor: '#10b981',
  },
  {
    regex: /\[CONFLICT:\s*([^\]]+)\]/g,
    markerName: 'conflict',
    label: 'CONFLICT',
    icon: '⚡',
    bgColor: '#fed7aa',
    textColor: '#9a3412',
    borderColor: '#f97316',
  },
  {
    regex: /\[ASSUMPTION:\s*([^\]]+)\]/g,
    markerName: 'assumption',
    label: 'ASSUMPTION',
    icon: '💡',
    bgColor: '#f3e8ff',
    textColor: '#6b21a8',
    borderColor: '#a855f7',
  },
  {
    regex: /\[SOURCE:\s*([^\]]+)\]/g,
    markerName: 'source',
    label: 'SOURCE',
    icon: '📄',
    bgColor: '#f3f4f6',
    textColor: '#374151',
    borderColor: '#9ca3af',
  },
  {
    regex: /\[DOCUMENTED:\s*([^\]]+)\]/g,
    markerName: 'documented',
    label: 'DOCUMENTED',
    icon: '📋',
    bgColor: '#dcfce7',
    textColor: '#166534',
    borderColor: '#22c55e',
  },
  {
    regex: /\[INFERRED:\s*([^\]]+)\]/g,
    markerName: 'inferred',
    label: 'INFERRED',
    icon: '🔍',
    bgColor: '#dbeafe',
    textColor: '#1e3a8a',
    borderColor: '#3b82f6',
  },
  {
    regex: /\[ASSUMED:\s*([^\]]+)\]/g,
    markerName: 'assumed',
    label: 'ASSUMED',
    icon: '💭',
    bgColor: '#fef3c7',
    textColor: '#92400e',
    borderColor: '#fbbf24',
  },
];

/**
 * Convert text markers like [CONTROL POINT: text] to styled HTML spans for TipTap editor
 */
export function convertMarkersToHTML(text: string): string {
  if (!text) return '<p></p>';

  let html = text;

  // First, normalize markers by removing bold formatting from marker labels
  // Convert [**DOCUMENTED:**text] to [DOCUMENTED:text]
  markerPatterns.forEach(pattern => {
    const labelWithBold = `\\*\\*${pattern.label}:\\*\\*`;
    const boldRegex = new RegExp(`\\[${labelWithBold}\\s*([^\\]]+)\\]`, 'g');
    html = html.replace(boldRegex, `[${pattern.label}: $1]`);
  });

  // Convert each marker pattern to HTML
  markerPatterns.forEach(pattern => {
    const regex = new RegExp(pattern.regex.source, 'g');
    html = html.replace(regex, (match, content) => {
      // Escape the content to prevent XSS
      const escapedContent = content
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

      return `<span data-marker="${pattern.markerName}" data-content="${escapedContent}"><span>${pattern.icon}</span><span><strong>${pattern.label}:</strong></span><span>${escapedContent}</span></span>`;
    });
  });

  // Convert **text** to <strong>text</strong> for proper bold display in editor
  html = html.replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>');

  // For content that lacks newlines, try to detect sentence boundaries and add breaks
  // This helps Basic SOPs that are stored as one continuous block
  if (!html.includes('\n')) {
    // Add line breaks after sentences (period followed by space and capital letter)
    // But avoid breaking inside markers
    html = html.replace(/\.\s+(?=[A-Z])/g, '.\n');

    // Add line breaks after markers followed by text
    html = html.replace(/(<\/span>)\s+(?=[A-Z])/g, '$1\n');
  }

  // Split by newlines and wrap each line in a paragraph tag
  // This preserves the line structure for TipTap
  const lines = html.split('\n');
  const paragraphs = lines.map(line => {
    const trimmedLine = line.trim();
    // If line is empty, create an empty paragraph for spacing
    if (!trimmedLine) {
      return '<p><br /></p>';
    }
    // Wrap non-empty lines in paragraph tags
    return `<p>${line}</p>`;
  });

  // Join all paragraphs
  html = paragraphs.join('');

  return html;
}

/**
 * Convert HTML markers back to text markers like [CONTROL POINT: text]
 */
export function convertHTMLToMarkers(html: string): string {
  if (!html) return '';

  let text = html;

  // IMPORTANT: Process in correct order to avoid losing content

  // 1. Convert each marker HTML back to text format FIRST (before removing tags)
  markerPatterns.forEach(pattern => {
    // Match the marker span with data-marker and data-content attributes
    const markerRegex = new RegExp(
      `<span[^>]*data-marker="${pattern.markerName}"[^>]*data-content="([^"]*)"[^>]*>.*?</span>`,
      'gi'
    );
    text = text.replace(markerRegex, (match, content) => {
      // Unescape the content
      const unescapedContent = content
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
      return `[${pattern.label}: ${unescapedContent}]`;
    });
  });

  // 2. TipTap converts <br> to <p> tags, so we need to handle both
  // Convert </p><p> to newline (paragraph breaks)
  text = text.replace(/<\/p>\s*<p>/gi, '\n');

  // 3. Convert <br> tags back to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');

  // 4. Remove opening and closing <p> tags (but keep the content)
  text = text.replace(/<p>/gi, '');
  text = text.replace(/<\/p>/gi, '');

  // 5. Convert <strong> tags back to **text** markdown format BEFORE removing all tags
  text = text.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');

  // 6. Remove any remaining HTML tags (including <em>, etc.)
  text = text.replace(/<[^>]+>/g, '');

  // 7. Unescape HTML entities
  text = text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&');

  // 8. Clean up excessive newlines but DON'T collapse all newlines!
  // Only reduce 3+ newlines to 2 newlines
  text = text.replace(/\n{3,}/g, '\n\n');

  // 9. Trim only leading/trailing whitespace
  text = text.trim();

  return text;
}

/**
 * Check if content has any markers
 */
export function hasMarkers(text: string): boolean {
  return markerPatterns.some(pattern => pattern.regex.test(text));
}

/**
 * Extract all markers from text
 */
export function extractMarkers(text: string): Array<{ type: string; content: string; position: number }> {
  const markers: Array<{ type: string; content: string; position: number }> = [];

  markerPatterns.forEach(pattern => {
    const regex = new RegExp(pattern.regex.source, 'g');
    let match;

    while ((match = regex.exec(text)) !== null) {
      markers.push({
        type: pattern.label,
        content: match[1],
        position: match.index,
      });
    }
  });

  return markers.sort((a, b) => a.position - b.position);
}
